/*
 * Shows the full cost of an order inside the cart, before checkout.
 *
 * Everything here is deterministic, so no address and no rate lookup is needed:
 *   - shipping is a flat rate per market, free above a threshold
 *   - tax is GST/HST only (the shop isn't registered for PST / QST / RST),
 *     charged on the subtotal PLUS shipping
 *   - US and international orders carry no tax at all, so their total is exact
 *
 * The one unknown is the Canadian province, which sets the GST/HST rate. We
 * guess it from the browser's time zone, let the shopper correct it, and
 * remember the choice. Quebec shares a time zone with Ontario, so Quebecers
 * will see Ontario until they pick — the selector is right there.
 *
 * This is a custom element on purpose: the cart drawer re-renders by assigning
 * innerHTML, which does not re-run inline scripts, but does upgrade elements.
 */

const CCS_STORAGE_KEY = 'nutrimal:ship-province';

// GST/HST by province. Update if the shop ever registers for provincial tax.
const CCS_TAX_RATES = {
  AB: 0.05,
  BC: 0.05,
  MB: 0.05,
  NB: 0.15,
  NL: 0.15,
  NS: 0.14,
  NT: 0.05,
  NU: 0.05,
  ON: 0.13,
  PE: 0.15,
  QC: 0.05,
  SK: 0.05,
  YT: 0.05,
};

const CCS_PROVINCE_NAMES = {
  AB: 'Alberta',
  BC: 'British Columbia',
  MB: 'Manitoba',
  NB: 'New Brunswick',
  NL: 'Newfoundland and Labrador',
  NS: 'Nova Scotia',
  NT: 'Northwest Territories',
  NU: 'Nunavut',
  ON: 'Ontario',
  PE: 'Prince Edward Island',
  QC: 'Quebec',
  SK: 'Saskatchewan',
  YT: 'Yukon',
};

const CCS_ZONE_TO_PROVINCE = {
  'America/St_Johns': 'NL',
  'America/Halifax': 'NS',
  'America/Glace_Bay': 'NS',
  'America/Moncton': 'NB',
  'America/Goose_Bay': 'NL',
  'America/Toronto': 'ON',
  'America/Nipigon': 'ON',
  'America/Thunder_Bay': 'ON',
  'America/Iqaluit': 'NU',
  'America/Rankin_Inlet': 'NU',
  'America/Resolute': 'NU',
  'America/Winnipeg': 'MB',
  'America/Rainy_River': 'ON',
  'America/Regina': 'SK',
  'America/Swift_Current': 'SK',
  'America/Edmonton': 'AB',
  'America/Cambridge_Bay': 'NU',
  'America/Yellowknife': 'NT',
  'America/Inuvik': 'NT',
  'America/Creston': 'BC',
  'America/Dawson_Creek': 'BC',
  'America/Fort_Nelson': 'BC',
  'America/Vancouver': 'BC',
  'America/Whitehorse': 'YT',
  'America/Dawson': 'YT',
};

function ccsReadStoredProvince() {
  try {
    const v = localStorage.getItem(CCS_STORAGE_KEY);
    return v && CCS_TAX_RATES[v] ? v : null;
  } catch (e) {
    return null;
  }
}

function ccsStoreProvince(code) {
  try {
    localStorage.setItem(CCS_STORAGE_KEY, code);
  } catch (e) {
    /* private mode — the selector still works for this session */
  }
}

function ccsGuessProvince() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (CCS_ZONE_TO_PROVINCE[zone]) return CCS_ZONE_TO_PROVINCE[zone];
  } catch (e) {
    /* fall through */
  }
  return 'ON';
}

class CartCostSummary extends HTMLElement {
  connectedCallback() {
    this.currency = this.dataset.currency || 'CAD';
    this.country = (this.dataset.country || '').toUpperCase();
    this.subtotal = parseInt(this.dataset.subtotal, 10) || 0;

    this.province = ccsReadStoredProvince() || ccsGuessProvince();
    this.render();

    this.addEventListener('change', (event) => {
      if (!event.target.matches('[data-ccs-province]')) return;
      this.province = event.target.value;
      ccsStoreProvince(this.province);
      this.render();
    });
  }

  money(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-CA', {
        style: 'currency',
        currency: this.currency,
        currencyDisplay: 'narrowSymbol',
      }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* Returns shipping in cents, or null when we can't price it here. */
  shipping() {
    const num = (key, fallback) => {
      const v = parseFloat(this.dataset[key]);
      return isNaN(v) ? fallback : v;
    };

    if (this.country === 'CA') {
      return this.subtotal >= num('freeCa', 90) * 100 ? 0 : num('shipCa', 10) * 100;
    }
    if (this.country === 'US') {
      return this.subtotal >= num('freeUs', 75) * 100 ? 0 : num('shipUs', 7) * 100;
    }
    return null;
  }

  render() {
    const shipping = this.shipping();
    const domestic = shipping !== null;
    const taxRate = this.country === 'CA' ? CCS_TAX_RATES[this.province] || 0 : 0;
    const tax = domestic ? Math.round((this.subtotal + shipping) * taxRate) : 0;
    const total = domestic ? this.subtotal + shipping + tax : this.subtotal;
    const intlRate = parseFloat(this.dataset.shipIntl);

    const rows = [];

    rows.push(this.row('Subtotal', this.money(this.subtotal)));

    if (domestic) {
      rows.push(
        this.row(
          'Shipping',
          shipping === 0
            ? '<span class="ccs__value--free">Free</span>'
            : this.money(shipping)
        )
      );
    } else {
      rows.push(
        this.row(
          'Shipping',
          '<span class="ccs__value--muted">Flat CA$' + (isNaN(intlRate) ? 50 : intlRate) + '</span>'
        )
      );
    }

    // US orders carry no tax; the note below says so in one line instead of a "Tax: None" row.
    if (this.country === 'CA') {
      rows.push(this.row('Tax (' + this.taxLabel() + ')', this.money(tax)));
    }

    rows.push(
      '<div class="ccs__row ccs__row--total">' +
        '<span class="ccs__label">' +
        (domestic ? 'Total' : 'Estimated total') +
        '</span>' +
        '<span class="ccs__value">' +
        this.money(total) +
        ' ' +
        this.currency +
        '</span>' +
        '</div>'
    );

    if (this.country === 'CA') rows.push(this.provincePicker());

    if (!domestic) {
      rows.push(
        '<p class="ccs__note">International shipping is a flat CA$' +
          (isNaN(intlRate) ? 50 : intlRate) +
          ', added at checkout.</p>'
      );
    } else {
      rows.push(
        '<p class="ccs__note">' +
          (this.country === 'US' ? 'No tax. Nothing else is added at checkout.' : 'Nothing else is added at checkout.') +
          '</p>'
      );
    }

    this.innerHTML = rows.join('');

    // Shopify injects the wallet buttons; they need re-initialising whenever the
    // drawer's markup is replaced.
    if (window.Shopify && window.Shopify.PaymentButton) {
      try {
        window.Shopify.PaymentButton.init();
      } catch (e) {
        /* already initialised */
      }
    }
  }

  taxLabel() {
    const rate = CCS_TAX_RATES[this.province] || 0;
    const name = ['ON', 'NB', 'NL', 'NS', 'PE'].indexOf(this.province) > -1 ? 'HST' : 'GST';
    return name + ' ' + Math.round(rate * 100) + '%';
  }

  row(label, value) {
    return (
      '<div class="ccs__row"><span class="ccs__label">' +
      label +
      '</span><span class="ccs__value">' +
      value +
      '</span></div>'
    );
  }

  provincePicker() {
    const options = Object.keys(CCS_PROVINCE_NAMES)
      .sort((a, b) => CCS_PROVINCE_NAMES[a].localeCompare(CCS_PROVINCE_NAMES[b]))
      .map(
        (code) =>
          '<option value="' +
          code +
          '"' +
          (code === this.province ? ' selected' : '') +
          '>' +
          CCS_PROVINCE_NAMES[code] +
          '</option>'
      )
      .join('');

    return (
      '<div class="ccs__ship-to">' +
      '<label class="ccs__ship-to-label" for="ccs-province-' +
      (this.dataset.scope || 'cart') +
      '">Shipping to</label>' +
      '<select class="ccs__select" id="ccs-province-' +
      (this.dataset.scope || 'cart') +
      '" data-ccs-province>' +
      options +
      '</select>' +
      '</div>'
    );
  }
}

if (!customElements.get('cart-cost-summary')) {
  customElements.define('cart-cost-summary', CartCostSummary);
}
